import { useEffect, useMemo, useRef, useState, type ChangeEvent, type CSSProperties, type ReactNode } from "react"
import {
  ExternalLink,
  GripHorizontal,
  Leaf,
  Sparkles,
  Sun,
  Upload,
} from "lucide-react"
import { Input } from "@/components/ui/input"
import { ColorField } from "@/components/shared/color-field"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ChatWidget } from "@/components/widget/chat-widget"
import { AvatarIcon } from "@/components/widget/avatar-icon"
import type { ChatbotSettings, InputShadow, LauncherStyle, SubmitColorStyle, WidgetMotion, WidgetPosition, WidgetSize, WidgetTheme } from "@/lib/types"
import { buildGradient, contrastColor, gradientContrastColor, launcherSurface, readableTextColor, resolveLauncherGradient, resolvePrimaryGradient, WIDGET_GRADIENT_PRESETS } from "@/lib/widget-theme"
import { cn } from "@/lib/utils"

type EditingTheme = "light" | "dark"

type ThemeVariant = {
  primary: string
  header: string
  launcher: string
  userMessage: string
  assistantMessage: string
  text: string
  background: string
  callout: string
}

type ThemePreset = { name: string; light: ThemeVariant; dark: ThemeVariant }

const themePresets: ThemePreset[] = [
  {
    name: "Clássico",
    light: { primary: "#FD8627", header: "#FFFFFF", launcher: "#EF6F00", userMessage: "#EF6F00", assistantMessage: "#F1F1F1", text: "#1B1B1B", background: "#FFFFFF", callout: "#EF6F00" },
    dark: { primary: "#FD8627", header: "#FFFFFF", launcher: "#EF6F00", userMessage: "#EF6F00", assistantMessage: "#F1F1F1", text: "#1B1B1B", background: "#FFFFFF", callout: "#EF6F00" },
  },
  {
    name: "Moderno",
    light: { primary: "#5E76D8", header: "#FFFFFF", launcher: "#5E76D8", userMessage: "#5E76D8", assistantMessage: "#FFFFFF", text: "#283044", background: "#F7F7FB", callout: "#5E76D8" },
    dark: { primary: "#5E76D8", header: "#5E76D8", launcher: "#5E76D8", userMessage: "#5E76D8", assistantMessage: "#191E26", text: "#F7F9FC", background: "#111419", callout: "#5E76D8" },
  },
  {
    name: "Profissional",
    light: { primary: "#334155", header: "#1E293B", launcher: "#334155", userMessage: "#E2E8F0", assistantMessage: "#FFFFFF", text: "#1E293B", background: "#F8FAFC", callout: "#334155" },
    dark: { primary: "#CBD5E1", header: "#0F172A", launcher: "#CBD5E1", userMessage: "#334155", assistantMessage: "#172033", text: "#F1F5F9", background: "#0B1220", callout: "#CBD5E1" },
  },
  {
    name: "Minimalista",
    light: { primary: "#64748B", header: "#FFFFFF", launcher: "#64748B", userMessage: "#E2E8F0", assistantMessage: "#FFFFFF", text: "#334155", background: "#F8FAFC", callout: "#64748B" },
    dark: { primary: "#CBD5E1", header: "#1E293B", launcher: "#CBD5E1", userMessage: "#334155", assistantMessage: "#111827", text: "#E5E7EB", background: "#0F1115", callout: "#CBD5E1" },
  },
  {
    name: "Oceano",
    light: { primary: "#0E7490", header: "#FFFFFF", launcher: "#0891B2", userMessage: "#CFFAFE", assistantMessage: "#FFFFFF", text: "#164E63", background: "#F0FDFA", callout: "#0E7490" },
    dark: { primary: "#67E8F9", header: "#164E63", launcher: "#22D3EE", userMessage: "#164E63", assistantMessage: "#102A33", text: "#ECFEFF", background: "#071A20", callout: "#67E8F9" },
  },
  {
    name: "Floresta",
    light: { primary: "#166534", header: "#14532D", launcher: "#16A34A", userMessage: "#DCFCE7", assistantMessage: "#FFFFFF", text: "#14532D", background: "#F0FDF4", callout: "#166534" },
    dark: { primary: "#86EFAC", header: "#14532D", launcher: "#4ADE80", userMessage: "#14532D", assistantMessage: "#102619", text: "#F0FDF4", background: "#08170D", callout: "#86EFAC" },
  },
  {
    name: "Pôr do sol",
    light: { primary: "#C2410C", header: "#9A3412", launcher: "#EA580C", userMessage: "#FFEDD5", assistantMessage: "#FFFFFF", text: "#7C2D12", background: "#FFF7ED", callout: "#C2410C" },
    dark: { primary: "#FDBA74", header: "#7C2D12", launcher: "#FB923C", userMessage: "#7C2D12", assistantMessage: "#2A1710", text: "#FFF7ED", background: "#1D100A", callout: "#FDBA74" },
  },
  {
    name: "Lavanda",
    light: { primary: "#6D28D9", header: "#FFFFFF", launcher: "#7C3AED", userMessage: "#EDE9FE", assistantMessage: "#FFFFFF", text: "#4C1D95", background: "#FAF5FF", callout: "#6D28D9" },
    dark: { primary: "#C4B5FD", header: "#4C1D95", launcher: "#A78BFA", userMessage: "#4C1D95", assistantMessage: "#1D1630", text: "#F5F3FF", background: "#100B1B", callout: "#C4B5FD" },
  },
  {
    name: "Rosa",
    light: { primary: "#BE123C", header: "#FFFFFF", launcher: "#E11D48", userMessage: "#FFE4E6", assistantMessage: "#FFFFFF", text: "#881337", background: "#FFF1F2", callout: "#BE123C" },
    dark: { primary: "#FDA4AF", header: "#881337", launcher: "#FB7185", userMessage: "#881337", assistantMessage: "#2A1118", text: "#FFF1F2", background: "#1D0C11", callout: "#FDA4AF" },
  },
  {
    name: "Âmbar",
    light: { primary: "#B45309", header: "#92400E", launcher: "#D97706", userMessage: "#FEF3C7", assistantMessage: "#FFFFFF", text: "#78350F", background: "#FFFBEB", callout: "#B45309" },
    dark: { primary: "#FCD34D", header: "#78350F", launcher: "#FBBF24", userMessage: "#78350F", assistantMessage: "#2A1B08", text: "#FFFBEB", background: "#1C1306", callout: "#FCD34D" },
  },
  {
    name: "Índigo",
    light: { primary: "#4338CA", header: "#3730A3", launcher: "#4F46E5", userMessage: "#E0E7FF", assistantMessage: "#FFFFFF", text: "#312E81", background: "#EEF2FF", callout: "#4338CA" },
    dark: { primary: "#A5B4FC", header: "#312E81", launcher: "#818CF8", userMessage: "#312E81", assistantMessage: "#12142D", text: "#EEF2FF", background: "#0B0D1D", callout: "#A5B4FC" },
  },
  {
    name: "Menta",
    light: { primary: "#047857", header: "#065F46", launcher: "#10B981", userMessage: "#D1FAE5", assistantMessage: "#FFFFFF", text: "#064E3B", background: "#ECFDF5", callout: "#047857" },
    dark: { primary: "#6EE7B7", header: "#064E3B", launcher: "#34D399", userMessage: "#064E3B", assistantMessage: "#0D241D", text: "#ECFDF5", background: "#071710", callout: "#6EE7B7" },
  },
  {
    name: "Coral",
    light: { primary: "#BE4B3A", header: "#FFFFFF", launcher: "#E76F51", userMessage: "#FDE2D5", assistantMessage: "#FFFFFF", text: "#6B2B24", background: "#FFF8F5", callout: "#BE4B3A" },
    dark: { primary: "#FDB7A5", header: "#6B2B24", launcher: "#F28C77", userMessage: "#6B2B24", assistantMessage: "#291513", text: "#FFF1EC", background: "#1B0E0C", callout: "#FDB7A5" },
  },
  {
    name: "Ardósia",
    light: { primary: "#475569", header: "#334155", launcher: "#64748B", userMessage: "#E2E8F0", assistantMessage: "#FFFFFF", text: "#1E293B", background: "#F8FAFC", callout: "#475569" },
    dark: { primary: "#CBD5E1", header: "#1E293B", launcher: "#94A3B8", userMessage: "#334155", assistantMessage: "#151B25", text: "#F1F5F9", background: "#0C1118", callout: "#CBD5E1" },
  },
  {
    name: "Petróleo",
    light: { primary: "#0F766E", header: "#115E59", launcher: "#14B8A6", userMessage: "#CCFBF1", assistantMessage: "#FFFFFF", text: "#134E4A", background: "#F0FDFA", callout: "#0F766E" },
    dark: { primary: "#5EEAD4", header: "#134E4A", launcher: "#2DD4BF", userMessage: "#134E4A", assistantMessage: "#0D2422", text: "#F0FDFA", background: "#071817", callout: "#5EEAD4" },
  },
  {
    name: "Céu",
    light: { primary: "#0369A1", header: "#FFFFFF", launcher: "#0284C7", userMessage: "#E0F2FE", assistantMessage: "#FFFFFF", text: "#0C4A6E", background: "#F0F9FF", callout: "#0369A1" },
    dark: { primary: "#7DD3FC", header: "#0C4A6E", launcher: "#38BDF8", userMessage: "#0C4A6E", assistantMessage: "#0C1E2B", text: "#F0F9FF", background: "#07131D", callout: "#7DD3FC" },
  },
  {
    name: "Ameixa",
    light: { primary: "#86198F", header: "#701A75", launcher: "#A21CAF", userMessage: "#FAE8FF", assistantMessage: "#FFFFFF", text: "#581C87", background: "#FDF4FF", callout: "#86198F" },
    dark: { primary: "#E879F9", header: "#581C87", launcher: "#D946EF", userMessage: "#581C87", assistantMessage: "#211126", text: "#FDF4FF", background: "#160A19", callout: "#E879F9" },
  },
  {
    name: "Cobalto",
    light: { primary: "#1D4ED8", header: "#1E40AF", launcher: "#2563EB", userMessage: "#DBEAFE", assistantMessage: "#FFFFFF", text: "#1E3A8A", background: "#EFF6FF", callout: "#1D4ED8" },
    dark: { primary: "#93C5FD", header: "#1E3A8A", launcher: "#60A5FA", userMessage: "#1E3A8A", assistantMessage: "#101B32", text: "#EFF6FF", background: "#08111F", callout: "#93C5FD" },
  },
  {
    name: "Sálvia",
    light: { primary: "#4D7C0F", header: "#FFFFFF", launcher: "#65A30D", userMessage: "#ECFCCB", assistantMessage: "#FFFFFF", text: "#365314", background: "#F7FEE7", callout: "#4D7C0F" },
    dark: { primary: "#BEF264", header: "#365314", launcher: "#A3E635", userMessage: "#365314", assistantMessage: "#17220A", text: "#F7FEE7", background: "#101707", callout: "#BEF264" },
  },
  {
    name: "Areia",
    light: { primary: "#A16207", header: "#FFFFFF", launcher: "#CA8A04", userMessage: "#FEF9C3", assistantMessage: "#FFFFFF", text: "#713F12", background: "#FEFCE8", callout: "#A16207" },
    dark: { primary: "#FDE68A", header: "#713F12", launcher: "#FACC15", userMessage: "#713F12", assistantMessage: "#241B08", text: "#FEFCE8", background: "#181305", callout: "#FDE68A" },
  },
  {
    name: "Grafite",
    light: { primary: "#262626", header: "#171717", launcher: "#404040", userMessage: "#E5E5E5", assistantMessage: "#FFFFFF", text: "#262626", background: "#FAFAFA", callout: "#262626" },
    dark: { primary: "#E5E5E5", header: "#262626", launcher: "#A3A3A3", userMessage: "#404040", assistantMessage: "#171717", text: "#FAFAFA", background: "#0F0F0F", callout: "#E5E5E5" },
  },
]


const DEFAULT_ASSISTANT_AVATAR = "/images/mil-play-assistant-avatar.png"
const themedAvatars = [
  { name: "Original", url: DEFAULT_ASSISTANT_AVATAR, color: "#EF6F00" },
  ...[
    ["Turquesa", "#0EA5A4"],
    ["Azul", "#2563EB"],
    ["Violeta", "#7C3AED"],
    ["Verde", "#16A34A"],
    ["Rosa", "#EC4899"],
    ["Dourado", "#EAB308"],
    ["Profissional", "#B91C1C"],
    ["Coral", "#E76F51"],
    ["Coral claro", "#E76F51"],
    ["Azul claro", "#2563EB"],
    ["Verde claro", "#65A30D"],
    ["Lilás", "#7C3AED"],
  ].map(([name, color], index) => ({
    name,
    url: "/images/chat-avatars/mascot-" + String(index + 1).padStart(2, "0") + ".jpg",
    color,
  })),
]
const launcherIconChoices = [{"value":"message","label":"Mensagem"},{"value":"bot","label":"Robô"},{"value":"headphones","label":"Suporte"},{"value":"sparkles","label":"Brilho"},{"value":"heart","label":"Coração"},{"value":"star","label":"Estrela"},{"value":"zap","label":"Raio"},{"value":"rocket","label":"Foguete"},{"value":"coffee","label":"Café"},{"value":"thumbsup","label":"Curtir"},{"value":"gem","label":"Diamante"},{"value":"smile","label":"Sorriso"},{"value":"sun","label":"Sol"},{"value":"leaf","label":"Folha"},{"value":"help","label":"Dúvida"},{"value":"phone","label":"Telefone"},{"value":"calendar","label":"Agenda"},{"value":"mail","label":"Email"},{"value":"briefcase","label":"Negócios"},{"value":"check","label":"Confirmar"},{"value":"send","label":"Enviar"}]

const inputClass = "field-bg mt-2 h-11 w-full rounded-xl border border-border px-3 text-[13px] outline-none transition focus:border-ring"
const cardClass = "panel-bg rounded-[22px] border border-border p-5"

function Field({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return <label className={cn("text-[12px] font-medium", className)}>{label}{children}</label>
}

function SectionHeading({ title, description }: { title: string; description?: string }) {
  return <><h2 className="font-display text-[19px] font-bold tracking-[-.02em]">{title}</h2>{description && <p className="muted mt-1 text-[12px]">{description}</p>}</>
}

function ToggleRow({ label, checked, onChange, disabled = false }: { label: string; checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean }) {
  return <label className={cn("flex items-center justify-between gap-4 text-[13px]", disabled && "opacity-60")}><span>{label}</span><Switch checked={checked} onCheckedChange={onChange} disabled={disabled} /></label>
}

const fontWeightOptions = [
  { value: 400, label: "Normal" },
  { value: 500, label: "Médio" },
  { value: 600, label: "Seminegrito" },
  { value: 700, label: "Negrito" },
] as const

function FontWeightPicker({ label, value, onChange }: { label: string; value: number; onChange: (next: 400 | 500 | 600 | 700) => void }) {
  return (
    <div>
      <p className="mb-2 text-[12px] font-medium">{label}</p>
      <div role="group" aria-label={label} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {fontWeightOptions.map((option) => (
          <button key={option.value} type="button" aria-pressed={value === option.value} onClick={() => onChange(option.value)} className={cn("min-h-10 rounded-lg border px-2 text-[11px] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", value === option.value ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent")} style={{ fontWeight: option.value }}>
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

type GradientStops = { start: string; end: string }

function GradientEditor({
  idPrefix,
  light,
  dark,
  editingTheme,
  angle,
  onLightChange,
  onDarkChange,
  onAngleChange,
  onPreset,
}: {
  idPrefix: string
  light: GradientStops
  dark: GradientStops
  editingTheme: EditingTheme
  angle: number
  onLightChange: (next: GradientStops) => void
  onDarkChange: (next: GradientStops) => void
  onAngleChange: (next: number) => void
  onPreset: (preset: (typeof WIDGET_GRADIENT_PRESETS)[number]) => void
}) {
  const activePreset = WIDGET_GRADIENT_PRESETS.find((preset) =>
    preset.start === light.start && preset.end === light.end &&
    preset.start === dark.start && preset.end === dark.end && preset.angle === angle,
  )
  const colors = editingTheme === "light" ? light : dark
  const onChange = editingTheme === "light" ? onLightChange : onDarkChange

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-[13px] font-semibold">Gradiente</h3>
          <p className="muted mt-1 text-[11px]">Predefinições atualizam claro e escuro; os campos abaixo editam o tema selecionado.</p>
        </div>
        <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-medium">
          {activePreset?.name ?? "Gradiente personalizado"}
        </span>
      </div>
      <div role="group" aria-label={`Predefinições de gradiente ${idPrefix}`} className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {WIDGET_GRADIENT_PRESETS.map((preset) => (
          <button
            key={preset.name}
            type="button"
            aria-label={`Aplicar gradiente ${preset.name} aos temas claro e escuro`}
            aria-pressed={activePreset?.name === preset.name}
            onClick={() => onPreset(preset)}
            className={cn("rounded-xl border p-2 text-left text-[10px] font-semibold transition hover:bg-accent/50", activePreset?.name === preset.name ? "border-primary bg-primary/10" : "border-border")}
          >
            <span aria-hidden="true" className="mb-1.5 block h-7 rounded-lg" style={{ background: buildGradient(preset.start, preset.end, preset.angle, true) }} />
            {preset.name}
          </button>
        ))}
      </div>
      <div className="mt-4">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[.14em]">{editingTheme === "light" ? "Tema claro" : "Tema escuro"}</p>
        <div className="grid gap-3 md:grid-cols-2">
          <ColorField id={`${idPrefix}-${editingTheme}-start`} label="Cor de início" value={colors.start} onChange={(start) => onChange({ ...colors, start })} />
          <ColorField id={`${idPrefix}-${editingTheme}-end`} label="Cor de fim" value={colors.end} onChange={(end) => onChange({ ...colors, end })} />
        </div>
      </div>
      <label htmlFor={`${idPrefix}-angle`} className="mt-4 block text-[12px] font-medium">
        <span className="flex items-center justify-between">Direção compartilhada do gradiente <output htmlFor={`${idPrefix}-angle`}>{angle}°</output></span>
        <input
          id={`${idPrefix}-angle`}
          type="range"
          min="0"
          max="360"
          step="1"
          value={angle}
          aria-label={`Direção do gradiente ${idPrefix}`}
          onChange={(event) => onAngleChange(Number(event.target.value))}
          className="mt-3 w-full accent-primary"
        />
      </label>
      <div aria-hidden="true" className="mt-3 h-2 rounded-full" style={{ background: buildGradient(colors.start, colors.end, angle, true) }} />
    </div>
  )
}

async function imageFileToDataUrl(file: File) {
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, 160 / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.round(bitmap.width * scale))
  canvas.height = Math.max(1, Math.round(bitmap.height * scale))
  const context = canvas.getContext("2d")
  if (!context) throw new Error("canvas")
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  return canvas.toDataURL("image/webp", 0.85)
}

export function AppearanceSection({ value, onChange }: { value: ChatbotSettings; onChange: (next: ChatbotSettings) => void }) {
  const [previewOpen, setPreviewOpen] = useState(true)
  const [editingTheme, setEditingTheme] = useState<EditingTheme>(() => value.widgetTheme === "dark" ? "dark" : "light")
  useEffect(() => { setEditingTheme(value.widgetTheme === "dark" ? "dark" : "light") }, [value.id, value.widgetTheme])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string>()
  const [launcherUploading, setLauncherUploading] = useState(false)
  const [launcherUploadError, setLauncherUploadError] = useState<string>()
  const [launcherIconDialogOpen, setLauncherIconDialogOpen] = useState(false)
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const launcherFileInputRef = useRef<HTMLInputElement>(null)
  const set = <K extends keyof ChatbotSettings>(key: K, next: ChatbotSettings[K]) => onChange({ ...value, [key]: next })
  const avatarColor = value.avatarColor || value.primaryColor
  const avatarPercent = value.avatarRadius >= 999 ? 50 : Math.min(50, Math.round(value.avatarRadius / 2))
  const activePreset = useMemo(() => {
    if (value.primaryGradientEnabled || value.userMessageTextColor || value.userMessageTextColorDark) return "custom"
    const index = themePresets.findIndex((preset) =>
      value.primaryColor === preset.light.primary &&
      value.headerColor === preset.light.header &&
      value.launcherColor === preset.light.launcher &&
      value.userMessageColor === preset.light.userMessage &&
      value.assistantMessageColor === preset.light.assistantMessage &&
      value.textColor === preset.light.text &&
      value.chatBackground === preset.light.background &&
      (value.calloutColor || value.primaryColor) === preset.light.callout &&
      value.primaryColorDark === preset.dark.primary &&
      value.headerColorDark === preset.dark.header &&
      value.launcherColorDark === preset.dark.launcher &&
      value.userMessageColorDark === preset.dark.userMessage &&
      value.assistantMessageColorDark === preset.dark.assistantMessage &&
      value.textColorDark === preset.dark.text &&
      value.chatBackgroundDark === preset.dark.background &&
      (value.calloutColorDark || value.primaryColorDark) === preset.dark.callout,
    )
    return index >= 0 ? String(index) : "custom"
  }, [value])
  const launcherStyle = value.launcherStyle
  const launcherShape = value.launcherRadius >= 999 ? "circle" : value.launcherRadius >= 38 ? "pill" : value.launcherRadius <= 10 ? "square" : "rounded"
  const launcherSize = value.launcherSize >= 70 ? "large" : value.launcherSize <= 52 ? "small" : "medium"
  const launcherCustom = /^data:image\//i.test(value.launcherIcon)
  const primaryGradientLight = resolvePrimaryGradient(value, false)
  const primaryGradientDark = resolvePrimaryGradient(value, true)
  const primaryGradientEnabled = value.primaryGradientEnabled === true
  const lightLauncherGradient = resolveLauncherGradient({
    launcherColor: value.launcherColor,
    primaryColor: primaryGradientEnabled ? primaryGradientLight.end : value.primaryColor,
    gradientStart: value.launcherGradientStart,
    gradientEnd: value.launcherGradientEnd,
    gradientAngle: value.launcherGradientAngle,
  })
  const darkLauncherGradient = resolveLauncherGradient({
    launcherColor: value.launcherColorDark,
    primaryColor: primaryGradientEnabled ? primaryGradientDark.end : value.primaryColorDark,
    gradientStart: value.launcherGradientStartDark ?? value.launcherGradientStart,
    gradientEnd: value.launcherGradientEndDark ?? value.launcherGradientEnd,
    gradientAngle: value.launcherGradientAngle,
  })
  const previewLauncherGradient = editingTheme === "dark" ? darkLauncherGradient : lightLauncherGradient
  const primaryAccentLight = readableTextColor(value.chatBackground, primaryGradientLight.solid)
  const primaryAccentDark = readableTextColor(value.chatBackgroundDark, primaryGradientDark.solid)
  const primaryAccent = editingTheme === "dark" ? primaryAccentDark : primaryAccentLight
  const automaticVisitorTextColorLight = primaryGradientEnabled
    ? gradientContrastColor(primaryGradientLight.start, primaryGradientLight.end)
    : contrastColor(value.userMessageColor)
  const automaticVisitorTextColorDark = primaryGradientEnabled
    ? gradientContrastColor(primaryGradientDark.start, primaryGradientDark.end)
    : contrastColor(value.userMessageColorDark)
  const automaticVisitorTextColor = editingTheme === "dark" ? automaticVisitorTextColorDark : automaticVisitorTextColorLight
  const previewColors = editingTheme === "dark"
    ? { launcherColor: value.launcherColorDark, primaryColor: value.primaryColorDark, assistantMessageColor: value.assistantMessageColorDark }
    : { launcherColor: value.launcherColor, primaryColor: value.primaryColor, assistantMessageColor: value.assistantMessageColor }
  const previewSettings = useMemo(() => ({ ...value, widgetTheme: editingTheme }), [value, editingTheme])
  const configuredLauncherPreview = launcherSurface(value.launcherStyle, {
    ...previewColors,
    gradientStart: previewLauncherGradient.start,
    gradientEnd: previewLauncherGradient.end,
    gradientAngle: value.launcherGradientAngle,
  })
  const launcherPreview = configuredLauncherPreview
  const launcherPreviewStyle: CSSProperties = {
    ...(launcherPreview.background ? { background: launcherPreview.background } : { backgroundColor: launcherPreview.backgroundColor }),
    color: launcherPreview.color,
    boxShadow: launcherPreview.boxShadow,
  }
  const widgetDimensions = { compact: { width: 344, height: 500 }, standard: { width: 327, height: 604 }, large: { width: 424, height: 650 } } as const
  const previewSize = {
    width: value.windowWidth ?? widgetDimensions[value.widgetSize].width,
    height: value.windowHeight ?? widgetDimensions[value.widgetSize].height,
  }

  function applyPreset(preset: ThemePreset) {
    const isFigmaClassicPreset = preset.name === "Clássico"
    onChange({
      ...value,
      primaryColor: preset.light.primary,
      primaryColorDark: preset.dark.primary,
      primaryGradientEnabled: isFigmaClassicPreset,
      primaryGradientStart: isFigmaClassicPreset ? "#EF6F00" : null,
      primaryGradientEnd: isFigmaClassicPreset ? "#EF9800" : null,
      primaryGradientStartDark: isFigmaClassicPreset ? "#EF6F00" : null,
      primaryGradientEndDark: isFigmaClassicPreset ? "#EF9800" : null,
      primaryGradientAngle: isFigmaClassicPreset ? 25 : 135,
      chatSubtitleColor: isFigmaClassicPreset ? "#3ABF38" : readableTextColor(preset.light.header, preset.light.primary),
      chatSubtitleColorDark: isFigmaClassicPreset ? "#3ABF38" : readableTextColor(preset.dark.header, preset.dark.primary),
      // A escolha do avatar é independente do tema; não substituímos a pessoa selecionada.
      avatarColor: value.avatarColor,
      headerGradientEnabled: false,
      headerColor: preset.light.header,
      headerColorDark: preset.dark.header,
      headerGradientStart: preset.light.header,
      headerGradientEnd: preset.light.primary,
      headerGradientStartDark: preset.dark.header,
      headerGradientEndDark: preset.dark.primary,
      headerGradientAngle: 135,
      launcherColor: preset.light.launcher,
      launcherColorDark: preset.dark.launcher,
      launcherStyle: isFigmaClassicPreset ? "gradient" : value.launcherStyle,
      launcherGradientStart: isFigmaClassicPreset ? "#EF6F00" : preset.light.launcher,
      launcherGradientEnd: isFigmaClassicPreset ? "#EF9800" : preset.light.primary,
      launcherGradientStartDark: isFigmaClassicPreset ? "#EF6F00" : preset.dark.launcher,
      launcherGradientEndDark: isFigmaClassicPreset ? "#EF9800" : preset.dark.primary,
      launcherGradientAngle: isFigmaClassicPreset ? 79 : 135,
      userMessageColor: preset.light.userMessage,
      userMessageColorDark: preset.dark.userMessage,
      userMessageTextColor: "",
      userMessageTextColorDark: "",
      assistantMessageColor: preset.light.assistantMessage,
      assistantMessageColorDark: preset.dark.assistantMessage,
      textColor: preset.light.text,
      textColorDark: preset.dark.text,
      chatBackground: preset.light.background,
      chatBackgroundDark: preset.dark.background,
      calloutColor: "",
      calloutColorDark: "",
    })
  }

  async function handleAvatarFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file) return
    if (!file.type.startsWith("image/")) return setUploadError("Envie um arquivo de imagem (PNG, JPG ou WebP).")
    setUploading(true)
    setUploadError(undefined)
    try {
      set("avatarUrl", await imageFileToDataUrl(file))
      setAvatarDialogOpen(false)
    } catch {
      setUploadError("Não foi possível processar essa imagem.")
    } finally {
      setUploading(false)
    }
  }

  async function handleLauncherFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file) return
    if (!file.type.startsWith("image/")) return setLauncherUploadError("Envie um arquivo de imagem (PNG, JPG ou WebP).")
    setLauncherUploading(true)
    setLauncherUploadError(undefined)
    try {
      set("launcherIcon", await imageFileToDataUrl(file))
      setLauncherIconDialogOpen(false)
    } catch {
      setLauncherUploadError("Não foi possível processar essa imagem.")
    } finally {
      setLauncherUploading(false)
    }
  }

  function applyLauncherGradientPreset(preset: (typeof WIDGET_GRADIENT_PRESETS)[number]) {
    onChange({
      ...value,
      launcherStyle: "gradient",
      launcherGradientStart: preset.start,
      launcherGradientEnd: preset.end,
      launcherGradientStartDark: preset.start,
      launcherGradientEndDark: preset.end,
      launcherGradientAngle: preset.angle,
    })
  }

  function applyHeaderGradientPreset(preset: (typeof WIDGET_GRADIENT_PRESETS)[number]) {
    onChange({
      ...value,
      headerGradientEnabled: true,
      headerGradientStart: preset.start,
      headerGradientEnd: preset.end,
      headerGradientStartDark: preset.start,
      headerGradientEndDark: preset.end,
      headerGradientAngle: preset.angle,
    })
  }

  function applyPrimaryGradientPreset(preset: (typeof WIDGET_GRADIENT_PRESETS)[number]) {
    onChange({
      ...value,
      primaryGradientEnabled: true,
      primaryGradientStart: preset.start,
      primaryGradientEnd: preset.end,
      primaryGradientStartDark: preset.start,
      primaryGradientEndDark: preset.end,
      primaryGradientAngle: preset.angle,
    })
  }

  return (
    <div className="grid items-start gap-6 xl:grid-cols-[minmax(340px,420px)_minmax(0,1fr)]">
      <div className="self-stretch">
        <div className="flex h-16 items-center justify-between gap-3 px-1">
          <div><p className="muted text-[10px] font-semibold uppercase tracking-[.16em]">Ao vivo</p><h2 className="mt-1 font-display text-[20px] font-bold">Pré-visualização do widget</h2></div>
          <button type="button" className={cn("flex h-8 items-center gap-2 rounded-full px-3 text-[11px] font-medium", previewOpen ? "bg-success/10 text-success" : "bg-warning/10 text-warning")} onClick={() => setPreviewOpen((current) => !current)}><span className={cn("size-1.5 rounded-full", previewOpen ? "bg-success" : "bg-warning")} />{previewOpen ? "Aberto" : "Fechado"}</button>
        </div>
        <section className="panel-bg sticky -top-8 z-10 rounded-[24px] border border-border p-5">
          <div
            className="relative mx-auto w-full overflow-hidden rounded-2xl border border-border text-foreground"
            data-preview-theme={editingTheme}
            style={{ maxWidth: previewSize.width + 24, height: `min(${previewOpen ? previewSize.height + value.launcherSize + 42 : Math.max(140, value.launcherSize + 60)}px, calc(100dvh - 8rem))` }}
          >
            <ChatWidget settings={previewSettings} preview contained open={previewOpen} onOpenChange={setPreviewOpen} previewWidth={previewSize.width} previewHeight={previewSize.height} />
          </div>
          <div className="mt-3 flex select-none items-center justify-center text-muted-foreground"><GripHorizontal className="size-4" /></div>
          <button type="button" className="mt-2 flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-border text-[12px] font-semibold transition hover:bg-accent hover:text-accent-foreground" onClick={() => setPreviewOpen((current) => !current)}><ExternalLink className="size-3.5" />{previewOpen ? "Fechar chat" : "Abrir chat para testar"}</button>
        </section>
      </div>

      <Tabs defaultValue="profile" className="min-w-0">
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-xl border border-border bg-card p-1 sm:grid-cols-5">
          {([
            ["profile", "Perfil"],
            ["theme", "Tema"],
            ["layout", "Layout"],
            ["launcher", "Botão"],
            ["messages", "Mensagens"],
          ] as [string, string][]).map(([tabValue, label]) => (
            <TabsTrigger key={tabValue} value={tabValue} className="h-9 rounded-lg px-2 text-[11px] font-semibold shadow-none data-[state=active]:bg-accent data-[state=active]:text-accent-foreground data-[state=active]:shadow-none">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="profile" className="mt-4 space-y-5">

        <section className={cardClass}>
          <SectionHeading title="Identidade do assistente" description="Personalize a identificação e o status exibidos no cabeçalho." />
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Nome do assistente (IA)"><Input value={value.assistantName} onChange={(event) => set("assistantName", event.target.value)} className={inputClass} /></Field>
            <Field label="Título do chat (cabeçalho)"><Input value={value.chatTitle} onChange={(event) => set("chatTitle", event.target.value)} className={inputClass} /></Field>
            <Field label="Subtítulo / status" className="sm:col-span-2"><Input value={value.chatSubtitle} onChange={(event) => set("chatSubtitle", event.target.value)} className={inputClass} /></Field>
          </div>
          <div className="mt-5">
            <FontWeightPicker label="Peso do título (ex.: Atendimento)" value={value.headerTitleFontWeight ?? 700} onChange={(next) => set("headerTitleFontWeight", next)} />
          </div>
        </section>


        <section className={cardClass}>
          <div className="flex items-center justify-between gap-4"><div><SectionHeading title="Configuração do avatar" description="Escolha um rosto reconhecível para o seu assistente." /></div><div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden" style={{ backgroundColor: `color-mix(in srgb, ${avatarColor} 15%, transparent)`, borderRadius: `${avatarPercent}%` }}>{value.avatarUrl ? (<img src={value.avatarUrl} alt="" className="size-full object-cover object-top" style={{ borderRadius: `${avatarPercent}%` }} />) : (<AvatarIcon avatar={value.avatar} className="size-5" style={{ color: avatarColor }} />)}</div></div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => setAvatarDialogOpen(true)}
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-border bg-card px-4 text-[12px] font-semibold hover:bg-accent">
              <Sparkles className="size-4" /> Escolher avatar ou ícone
            </button>
            <button type="button" onClick={() => onChange({ ...value, avatarUrl: DEFAULT_ASSISTANT_AVATAR, avatar: "sparkles", avatarColor: "#EF6F00", avatarRadius: 999 })}
              className="h-11 rounded-xl border border-border px-3 text-[12px] font-semibold hover:bg-accent">
              Restaurar padrão
            </button>
          </div>
          <Dialog open={avatarDialogOpen} onOpenChange={setAvatarDialogOpen}>
            <DialogContent className="max-h-[90dvh] w-[min(94vw,780px)] max-w-[780px] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Avatar do assistente</DialogTitle>
                <DialogDescription>Escolha um personagem, um ícone ou envie sua própria imagem. A escolha não depende do tema.</DialogDescription>
              </DialogHeader>
              <Tabs defaultValue="characters" className="mt-3 min-w-0">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="characters">Personagens</TabsTrigger>
                  <TabsTrigger value="icons">Ícones</TabsTrigger>
                  <TabsTrigger value="upload">Minha imagem</TabsTrigger>
                </TabsList>
                <TabsContent value="characters" className="mt-4">
                  <div className="grid max-h-[45dvh] grid-cols-3 gap-3 overflow-y-auto p-1 sm:grid-cols-4 md:grid-cols-5">
                    {themedAvatars.map(({ name, url, color }) => (
                      <button key={url} type="button" title={name}
                        aria-label={"Selecionar personagem " + name}
                        aria-pressed={value.avatarUrl === url}
                        onClick={() => { onChange({ ...value, avatarUrl: url, avatarColor: color, avatarRadius: 999 }); setAvatarDialogOpen(false) }}
                        className={cn("group min-w-0 overflow-hidden rounded-xl border-2 text-left transition hover:border-primary/60", value.avatarUrl === url ? "border-primary ring-2 ring-primary/20" : "border-border")}>
                        <img src={url} alt="" className="aspect-square w-full object-cover object-top" loading="lazy" />
                        <p className="truncate px-2 py-1.5 text-center text-[10px] font-semibold">{name}</p>
                      </button>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="icons" className="mt-4">
                  <p className="mb-3 text-[12px] text-muted-foreground">O ícone usa a cor definida no perfil do assistente.</p>
                  <div className="grid max-h-[45dvh] grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-6">
                    {launcherIconChoices.map(({ value: icon, label }) => (
                      <button key={icon} type="button" aria-label={"Selecionar ícone " + label}
                        aria-pressed={!value.avatarUrl && value.avatar === icon}
                        onClick={() => { onChange({ ...value, avatarUrl: "", avatar: icon }); setAvatarDialogOpen(false) }}
                        className={cn("flex min-h-20 flex-col items-center justify-center gap-2 rounded-xl border transition hover:bg-accent", !value.avatarUrl && value.avatar === icon ? "border-primary bg-primary/10" : "border-border")}>
                        <AvatarIcon avatar={icon} className="size-6" style={{ color: avatarColor }} />
                        <span className="text-[10px] font-medium">{label}</span>
                      </button>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="upload" className="mt-4">
                  <div className="rounded-2xl border border-dashed border-border p-6 text-center">
                    <p className="text-[13px] font-semibold">Envie uma imagem do seu dispositivo</p>
                    <p className="muted mt-2 text-[12px]">PNG, JPG ou WebP. A foto fica salva no perfil do assistente.</p>
                    <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()}
                      className="mt-5 inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-4 text-[12px] font-semibold text-primary-foreground">
                      <Upload className="size-4" />{uploading ? "Processando..." : "Selecionar imagem"}
                    </button>
                    {uploadError && <p className="mt-3 text-xs text-destructive">{uploadError}</p>}
                  </div>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(event) => void handleAvatarFile(event)} />
          {uploadError && <p className="mt-2 text-[11px] text-destructive">{uploadError}</p>}
          <div className="mt-5"><label className="flex items-center justify-between text-[12px] font-medium">Arredondamento do avatar <output>{avatarPercent}%</output></label><input type="range" min="0" max="50" value={avatarPercent} onChange={(event) => set("avatarRadius", Number(event.target.value) >= 50 ? 999 : Number(event.target.value) * 2)} className="mt-3 w-full accent-primary" /></div>
          <div className="mt-5"><ColorField id="avatar-color" label="Cor padrão do avatar" value={avatarColor} onChange={(next) => set("avatarColor", next)} /></div>
        </section>
        </TabsContent>

        <TabsContent value="theme" className="mt-4 space-y-5">
          <section className={cardClass}>
            <SectionHeading title="Predefinições" description="Cada combinação aplica cores para os temas claro e escuro." />
            <div role="group" aria-label="Predefinições de cores, aplicadas aos temas claro e escuro" className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {themePresets.map((preset, index) => (
                <button key={preset.name} type="button" onClick={() => applyPreset(preset)} aria-label={`Aplicar predefinição ${preset.name} aos temas claro e escuro`} aria-pressed={activePreset === String(index)} className={cn("min-w-0 rounded-xl border p-2 text-left text-[11px] font-semibold transition", activePreset === String(index) ? "border-primary bg-primary/10" : "border-border hover:bg-accent/50")}>
                  <span aria-hidden="true" className="mb-2 flex gap-1"><span className="h-2 flex-1 rounded-full" style={{ backgroundColor: preset.light.primary }} /><span className="h-2 flex-1 rounded-full" style={{ backgroundColor: preset.dark.primary }} /></span>
                  <span className="block truncate">{preset.name}</span>
                </button>
              ))}
            </div>
            <p aria-live="polite" className="muted mt-3 text-[11px]">
              {activePreset === "custom" ? "Combinação atual: personalizada." : `Predefinição ativa: ${themePresets[Number(activePreset)]?.name}.`}
            </p>
            <div className="mt-5 border-t border-border pt-4">
              <p className="text-[12px] font-semibold">Modo de exibição no site</p>
              <p className="muted mt-1 text-[11px]">Define o modo publicado. O preview do editor acompanha a paleta selecionada abaixo.</p>
              <div role="group" aria-label="Modo de exibição do widget" className="mt-3 flex rounded-xl border border-border p-1">
                {(["light", "dark", "auto"] as WidgetTheme[]).map((mode) => <button key={mode} type="button" onClick={() => set("widgetTheme", mode)} aria-pressed={value.widgetTheme === mode} className={cn("flex-1 rounded-lg py-2 text-[11px] font-semibold", value.widgetTheme === mode ? "bg-accent text-accent-foreground" : "muted")}>{mode === "light" ? "Claro" : mode === "dark" ? "Escuro" : "Automático"}</button>)}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
              <div>
                <p className="text-[12px] font-semibold">Editar paleta</p>
                <p className="muted mt-1 text-[11px]">O preview acompanha a seleção; isso não altera o modo publicado.</p>
              </div>
              <div role="group" aria-label="Paleta em edição" className="flex shrink-0 rounded-xl border border-border p-1">
                {(["light", "dark"] as const).map((mode) => <button key={mode} type="button" onClick={() => setEditingTheme(mode)} aria-pressed={editingTheme === mode} className={cn("min-w-20 rounded-lg px-3 py-2 text-[11px] font-semibold", editingTheme === mode ? "bg-accent text-accent-foreground" : "muted")}>{mode === "light" ? "Claro" : "Escuro"}</button>)}
              </div>
            </div>
          </section>

          <section className={cardClass}>
            <SectionHeading title="Destaques e mensagens" description="Cor principal, bolhas e texto do chat no tema selecionado." />
            <div className="mt-4 rounded-xl border border-border p-3">
              <ToggleRow label="Aplicar gradiente primário aos destaques do chat" checked={primaryGradientEnabled} onChange={(checked) => set("primaryGradientEnabled", checked)} />
              <p className="muted mt-2 text-[11px]">Quando ativo, o gradiente aparece no título do cabeçalho e nos demais elementos ligados à cor primária. O envio usa o gradiente no estilo Primário; o estilo Neutro mantém sua cor. Cabeçalho, botão flutuante e avatar preservam cores próprias; caret e rolagem seguem sólidos por legibilidade.</p>
            </div>
            {primaryGradientEnabled && (
              <GradientEditor
                idPrefix="primary-gradient"
                light={{ start: primaryGradientLight.start, end: primaryGradientLight.end }}
                dark={{ start: primaryGradientDark.start, end: primaryGradientDark.end }}
                editingTheme={editingTheme}
                angle={primaryGradientLight.angle}
                onLightChange={(next) => onChange({ ...value, primaryGradientStart: next.start, primaryGradientEnd: next.end })}
                onDarkChange={(next) => onChange({ ...value, primaryGradientStartDark: next.start, primaryGradientEndDark: next.end })}
                onAngleChange={(next) => set("primaryGradientAngle", next)}
                onPreset={applyPrimaryGradientPreset}
              />
            )}
            <div className="mt-4 space-y-4">
              <p className="text-[11px] font-semibold uppercase tracking-[.14em]">{editingTheme === "light" ? "Tema claro" : "Tema escuro"}</p>
              {primaryGradientEnabled ? (
                <div className="flex items-center gap-3 rounded-xl border border-border p-3">
                  <span aria-hidden="true" className="size-8 shrink-0 rounded-lg border border-border" style={{ backgroundColor: primaryAccent }} />
                  <span className="text-[11px]">Caret e rolagem mantêm uma cor sólida: {primaryAccent}</span>
                </div>
              ) : <ColorField id={`${editingTheme}-primary-color`} label="Cor primária" value={editingTheme === "dark" ? value.primaryColorDark : value.primaryColor} onChange={(next) => set(editingTheme === "dark" ? "primaryColorDark" : "primaryColor", next)} />}
              <ColorField
                id={`${editingTheme}-user-message-color`}
                label="Bolha do visitante"
                description={primaryGradientEnabled ? "Usa o gradiente primário; a cor salva continua preservada." : undefined}
                value={editingTheme === "dark" ? value.userMessageColorDark : value.userMessageColor}
                onChange={(next) => set(editingTheme === "dark" ? "userMessageColorDark" : "userMessageColor", next)}
                disabled={primaryGradientEnabled}
              />
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
                <ColorField
                  id={`${editingTheme}-user-message-text-color`}
                  label="Texto da bolha do visitante"
                  description={editingTheme === "dark" ? (value.userMessageTextColorDark ? "Cor personalizada, aplicada como escolhida." : `Automático: ${automaticVisitorTextColorDark}`) : (value.userMessageTextColor ? "Cor personalizada, aplicada como escolhida." : `Automático: ${automaticVisitorTextColorLight}`)}
                  value={editingTheme === "dark" ? (value.userMessageTextColorDark ?? "") : (value.userMessageTextColor ?? "")}
                  fallback={automaticVisitorTextColor}
                  onChange={(next) => set(editingTheme === "dark" ? "userMessageTextColorDark" : "userMessageTextColor", next)}
                />
                <button
                  type="button"
                  onClick={() => set(editingTheme === "dark" ? "userMessageTextColorDark" : "userMessageTextColor", "")}
                  disabled={!(editingTheme === "dark" ? value.userMessageTextColorDark : value.userMessageTextColor)}
                  aria-label={`Restaurar texto automático da bolha no tema ${editingTheme === "dark" ? "escuro" : "claro"}`}
                  className="mb-0.5 h-9 rounded-lg border border-border px-2.5 text-[11px] font-semibold hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                >Automático</button>
              </div>
              <ColorField id={`${editingTheme}-assistant-message-color`} label="Bolha do assistente" value={editingTheme === "dark" ? value.assistantMessageColorDark : value.assistantMessageColor} onChange={(next) => set(editingTheme === "dark" ? "assistantMessageColorDark" : "assistantMessageColor", next)} />
              <div className="grid gap-4 md:grid-cols-2">
                <ColorField id={`${editingTheme}-text-color`} label="Texto do chat" value={editingTheme === "dark" ? value.textColorDark : value.textColor} onChange={(next) => set(editingTheme === "dark" ? "textColorDark" : "textColor", next)} />
                <ColorField id={`subtitle-color-${editingTheme}`} label="Cor do status no cabeçalho" value={editingTheme === "dark" ? (value.chatSubtitleColorDark || "#FFFFFF") : (value.chatSubtitleColor || "#3ABF38")} onChange={(next) => set(editingTheme === "dark" ? "chatSubtitleColorDark" : "chatSubtitleColor", next)} />
              </div>
            </div>
          </section>

          <section className={cardClass}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <SectionHeading title="Cabeçalho do chat" description="A cor e o gradiente do cabeçalho são independentes da cor primária." />
              <ToggleRow label="Gradiente próprio do cabeçalho" checked={Boolean(value.headerGradientEnabled)} onChange={(checked) => set("headerGradientEnabled", checked)} />
            </div>
            {value.headerGradientEnabled ? (
              <GradientEditor
                idPrefix="header-gradient"
                light={{ start: value.headerGradientStart ?? value.headerColor, end: value.headerGradientEnd ?? (primaryGradientEnabled ? primaryGradientLight.end : value.primaryColor) }}
                dark={{ start: value.headerGradientStartDark ?? value.headerColorDark, end: value.headerGradientEndDark ?? (primaryGradientEnabled ? primaryGradientDark.end : value.primaryColorDark) }}
                editingTheme={editingTheme}
                angle={value.headerGradientAngle ?? 135}
                onLightChange={(next) => onChange({ ...value, headerGradientStart: next.start, headerGradientEnd: next.end })}
                onDarkChange={(next) => onChange({ ...value, headerGradientStartDark: next.start, headerGradientEndDark: next.end })}
                onAngleChange={(next) => set("headerGradientAngle", next)}
                onPreset={applyHeaderGradientPreset}
              />
            ) : (
              <div className="mt-4 max-w-xl">
                <ColorField id={`${editingTheme}-header-color`} label="Cor do cabeçalho" value={editingTheme === "dark" ? value.headerColorDark : value.headerColor} onChange={(next) => set(editingTheme === "dark" ? "headerColorDark" : "headerColor", next)} />
              </div>
            )}
          </section>

          <section className={cardClass}>
            <SectionHeading title="Fundo da conversa" description="Defina a superfície atrás das mensagens no tema selecionado." />
            <div className="mt-4 max-w-xl">
              <ColorField id={`${editingTheme}-chat-background`} label="Cor de fundo" value={editingTheme === "dark" ? value.chatBackgroundDark : value.chatBackground} onChange={(next) => set(editingTheme === "dark" ? "chatBackgroundDark" : "chatBackground", next)} />
            </div>
          </section>

          <section className={cardClass}>
            <SectionHeading title="Botão flutuante" description="Estilo, cores e gradiente do botão são independentes da cor primária." />
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Estilo do botão"><Select value={launcherStyle} onValueChange={(next) => set("launcherStyle", next as LauncherStyle)}><SelectTrigger className={inputClass}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="solid">Sólido</SelectItem><SelectItem value="soft">Suave</SelectItem><SelectItem value="gradient">Gradiente</SelectItem><SelectItem value="outline">Contorno</SelectItem></SelectContent></Select></Field>
            </div>
            {launcherStyle === "gradient" ? (
              <GradientEditor
                idPrefix="launcher-gradient"
                light={{ start: lightLauncherGradient.start, end: lightLauncherGradient.end }}
                dark={{ start: darkLauncherGradient.start, end: darkLauncherGradient.end }}
                editingTheme={editingTheme}
                angle={value.launcherGradientAngle ?? 135}
                onLightChange={(next) => onChange({ ...value, launcherGradientStart: next.start, launcherGradientEnd: next.end })}
                onDarkChange={(next) => onChange({ ...value, launcherGradientStartDark: next.start, launcherGradientEndDark: next.end })}
                onAngleChange={(next) => set("launcherGradientAngle", next)}
                onPreset={applyLauncherGradientPreset}
              />
            ) : (
              <div className="mt-4 max-w-xl">
                <ColorField id={`${editingTheme}-launcher-color`} label="Cor do botão" value={editingTheme === "dark" ? value.launcherColorDark : value.launcherColor} onChange={(next) => set(editingTheme === "dark" ? "launcherColorDark" : "launcherColor", next)} />
              </div>
            )}
            <div className="mt-5 border-t border-border pt-4">
              <ToggleRow label="Brilho ao redor do botão" checked={value.launcherGlowEnabled !== false} onChange={(checked) => set("launcherGlowEnabled", checked)} />
              {value.launcherGlowEnabled !== false ? (
                <div className="mt-3 max-w-xl">
                  <ColorField id={`${editingTheme}-launcher-glow`} label="Cor do brilho" value={editingTheme === "dark" ? (value.launcherGlowColorDark ?? value.launcherGlowColor ?? "#FFE000") : (value.launcherGlowColor ?? "#FFE000")} onChange={(next) => set(editingTheme === "dark" ? "launcherGlowColorDark" : "launcherGlowColor", next)} />
                </div>
              ) : null}
            </div>
            <div className="mt-5 border-t border-border pt-4">
              <p className="mb-3 text-[12px] font-semibold">Balão de chamada</p>
              <div className="mb-4 rounded-xl border border-border px-3 py-3">
                <ToggleRow
                  label="Usar gradiente do botão neste tema"
                  checked={!(editingTheme === "dark" ? value.calloutColorDark : value.calloutColor)}
                  onChange={(checked) => set(editingTheme === "dark" ? "calloutColorDark" : "calloutColor", checked ? "" : (editingTheme === "dark" ? value.primaryColorDark : value.primaryColor))}
                />
              </div>
              <div className="max-w-xl">
                <ColorField
                  id={`${editingTheme}-callout-color`}
                  label="Cor própria do balão"
                  description="Vazio usa o gradiente do botão neste tema."
                  value={editingTheme === "dark" ? value.calloutColorDark : value.calloutColor}
                  fallback={editingTheme === "dark" ? darkLauncherGradient.end : lightLauncherGradient.end}
                  onChange={(next) => set(editingTheme === "dark" ? "calloutColorDark" : "calloutColor", next)}
                />
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="layout" className="mt-4 space-y-5">
        <section className={cardClass}>
          <SectionHeading title="Posição e tamanho" description="Escolha um tamanho predefinido e mantenha o botão ancorado na parte inferior." />
          <div className="mt-5">
            <p className="text-[11px] font-semibold uppercase tracking-[.14em]">Tamanho da janela</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {([
                ["compact", "Compacto", "344 × 500", 344, 500],
                ["standard", "Padrão", "327 × 604", 327, 604],
                ["large", "Grande", "424 × 650", 424, 650],
              ] as [WidgetSize, string, string, number, number][]).map(([size, label, dimensions, width, height]) => (
                <button key={size} type="button" onClick={() => onChange({ ...value, widgetSize: size, windowWidth: width, windowHeight: height })} className={cn("rounded-xl border px-2 py-3 text-[11px] font-semibold transition", value.widgetSize === size ? "border-primary bg-primary/10" : "border-border hover:bg-accent/50")}>
                  <span className="block">{label}</span><span className="muted mt-1 block text-[10px] font-normal">{dimensions}px</span>
                </button>
              ))}
            </div>
          </div>
          <div className="mt-5 border-t border-border pt-4">
            <p className="text-[11px] font-semibold uppercase tracking-[.14em]">Posição do botão</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {([["bottom-right", "Inferior direito"], ["bottom-left", "Inferior esquerdo"]] as [WidgetPosition, string][]).map(([position, label]) => <button key={position} type="button" onClick={() => set("position", position)} className={cn("rounded-xl border py-3 text-[11px] font-semibold", value.position === position ? "border-primary bg-primary/10" : "border-border")}>{label}</button>)}
            </div>
          </div>
          <div className="mt-5 border-t border-border pt-4"><label className="flex items-center justify-between text-[12px] font-medium">Arredondamento do chat e dos controles <output>{value.widgetRadius}px</output></label><input type="range" min="0" max="40" value={value.widgetRadius} onChange={(event) => set("widgetRadius", Number(event.target.value))} className="mt-3 w-full accent-primary" /></div>
        </section>

        <section className={cardClass}><SectionHeading title="Marca" /><div className="mt-4 space-y-4"><ToggleRow label="Mostrar marca BubbleDesk" checked={value.showBranding} onChange={(checked) => set("showBranding", checked)} /><ToggleRow label='Mostrar link "Powered by"' checked={value.showPoweredBy ?? true} onChange={(checked) => set("showPoweredBy", checked)} /></div></section>
        </TabsContent>

        <TabsContent value="launcher" className="mt-4 space-y-5">
        <section className={cardClass}>
          <SectionHeading title="Comportamento e ícone do botão" description="Formato, tamanho e animação do botão flutuante." />
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Formato do botão"><Select value={launcherShape} onValueChange={(next) => set("launcherRadius", next === "circle" ? 999 : next === "pill" ? 40 : next === "square" ? 8 : 22)}><SelectTrigger className={inputClass}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="circle">Circular</SelectItem><SelectItem value="rounded">Arredondado</SelectItem><SelectItem value="square">Quadrado</SelectItem><SelectItem value="pill">Pílula</SelectItem></SelectContent></Select></Field>
            <Field label="Tamanho do botão"><Select value={launcherSize} onValueChange={(next) => set("launcherSize", next === "large" ? 72 : next === "small" ? 52 : 64)}><SelectTrigger className={inputClass}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="medium">Médio</SelectItem><SelectItem value="small">Pequeno</SelectItem><SelectItem value="large">Grande</SelectItem></SelectContent></Select></Field>
            <Field label="Animação"><Select value={value.motionStyle ?? "smooth"} onValueChange={(next) => set("motionStyle", next as WidgetMotion)}><SelectTrigger className={inputClass}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="smooth">Suave</SelectItem><SelectItem value="spring">Elástica</SelectItem><SelectItem value="slide">Deslizar</SelectItem><SelectItem value="pop">Pop</SelectItem><SelectItem value="fade">Esmaecer</SelectItem><SelectItem value="flip">Virar</SelectItem><SelectItem value="zoom">Zoom</SelectItem><SelectItem value="snappy">Ágil</SelectItem><SelectItem value="none">Nenhuma</SelectItem></SelectContent></Select></Field>
          </div>

          <div className="mt-4 flex items-center gap-4 rounded-2xl border border-border p-3">
            <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-full border" style={launcherPreviewStyle}>
              <AvatarIcon avatar={value.launcherIcon} className="size-7" />
            </div>
            <div className="min-w-0 flex-1"><p className="text-[12px] font-semibold">Ícone do botão</p><p className="muted text-[11px]">Escolha entre os ícones ou envie uma imagem.</p></div>
            <button type="button" onClick={() => setLauncherIconDialogOpen(true)} className="h-10 shrink-0 rounded-xl border border-border px-3 text-[12px] font-semibold hover:bg-accent">Trocar ícone</button>
          </div>
          <Dialog open={launcherIconDialogOpen} onOpenChange={setLauncherIconDialogOpen}>
            <DialogContent className="w-[min(94vw,760px)] max-w-[760px]">
              <DialogHeader>
                <DialogTitle>Ícone do botão de chat</DialogTitle>
                <DialogDescription>Escolha um ícone ou use uma imagem própria. O botão mantém o formato e as cores definidos acima.</DialogDescription>
              </DialogHeader>
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <p className="mb-3 text-[12px] font-semibold">Biblioteca de ícones</p>
                  <div className="grid max-h-64 grid-cols-4 gap-2 overflow-y-auto pr-1 sm:grid-cols-5">
                    {launcherIconChoices.map(({ value: icon, label }) => (
                      <button type="button" key={icon} title={label} aria-label={"Usar ícone " + label}
                        aria-pressed={value.launcherIcon === icon}
                        onClick={() => { set("launcherIcon", icon); setLauncherIconDialogOpen(false) }}
                        className={cn("flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border transition hover:bg-accent", value.launcherIcon === icon ? "border-primary bg-primary/10" : "border-border")}>
                        <AvatarIcon avatar={icon} className="size-5" /><span className="text-[9px]">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl border border-dashed border-border p-4">
                  <p className="text-[12px] font-semibold">Ícone personalizado</p>
                  <p className="muted mt-1 text-[11px]">PNG, JPG ou WebP. A imagem será usada no botão flutuante.</p>
                  <div className="mt-5 flex justify-center">
                    <div className="flex size-20 items-center justify-center overflow-hidden rounded-full border border-border bg-muted">
                      <AvatarIcon avatar={launcherCustom ? value.launcherIcon : "message"} className="size-12" />
                    </div>
                  </div>
                  <button type="button" onClick={() => launcherFileInputRef.current?.click()} disabled={launcherUploading}
                    className="mt-5 flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-border text-[12px] font-semibold hover:bg-accent">
                    <Upload className="size-4" />{launcherUploading ? "Processando..." : "Enviar ícone personalizado"}
                  </button>
                  <input ref={launcherFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(event) => void handleLauncherFile(event)} />
                  {launcherUploadError && <p className="mt-2 text-[11px] text-destructive">{launcherUploadError}</p>}
                </div>
              </div>
            </DialogContent>
          </Dialog>

        </section>
        <section className={cardClass}>
          <SectionHeading title="Balão de chamada" description="Mensagem sobre o botão para chamar atenção do visitante." />
          <div className="mt-4"><ToggleRow label="Exibir balão de chamada" checked={value.calloutEnabled} onChange={(checked) => set("calloutEnabled", checked)} /></div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Título do balão"><Input value={value.calloutTitle} maxLength={60} onChange={(event) => set("calloutTitle", event.target.value)} className={inputClass} placeholder="Melhor tarifa aqui!" /></Field>
            <Field label="Subtítulo do balão"><Input value={value.calloutSubtitle} maxLength={80} onChange={(event) => set("calloutSubtitle", event.target.value)} className={inputClass} placeholder="Atendimento 24h" /></Field>
          </div>
          <div className="mt-4"><label className="flex items-center justify-between text-[12px] font-medium">Atraso de exibição <output>{value.calloutDelaySeconds}s</output></label><input type="range" min="0" max="60" value={value.calloutDelaySeconds} onChange={(event) => set("calloutDelaySeconds", Number(event.target.value))} className="mt-3 w-full accent-primary" /></div>
        </section>
        </TabsContent>

        <TabsContent value="messages" className="mt-4">
        <section className={cardClass}><SectionHeading title="Mensagens do chat" /><Field label={<span className="mt-4 block">Mensagem de boas-vindas</span>}><Textarea maxLength={180} value={value.welcomeMessage.slice(0, 180)} onChange={(event) => set("welcomeMessage", event.target.value)} className="field-bg mt-2 min-h-[92px] w-full resize-y rounded-xl border border-border p-3 text-[13px] outline-none focus:border-ring" /><span className="muted mt-2 block text-right text-[11px]">{value.welcomeMessage.length}/180 caracteres</span></Field><Field label={<span className="mt-3 block">Texto de espaço reservado do campo</span>}><Input value={value.placeholder} onChange={(event) => set("placeholder", event.target.value)} className={inputClass} /></Field><div className="mt-5 border-t border-border pt-4"><p className="text-[12px] font-semibold">Controles do campo</p><div className="mt-3 grid gap-3 sm:grid-cols-2"><Field label="Cor do botão de envio"><Select value={value.submitColorStyle} onValueChange={(next) => set("submitColorStyle", next as SubmitColorStyle)}><SelectTrigger className={inputClass}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="primary">Primária</SelectItem><SelectItem value="neutral">Neutra</SelectItem></SelectContent></Select></Field><Field label="Sombra do campo"><Select value={value.inputShadow} onValueChange={(next) => set("inputShadow", next as InputShadow)}><SelectTrigger className={inputClass}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Nenhuma</SelectItem><SelectItem value="subtle">Sutil</SelectItem><SelectItem value="strong">Forte</SelectItem></SelectContent></Select></Field><div className="space-y-2">
              <p className="text-[12px] font-medium">Tamanho do texto da conversa</p>
              <div role="group" aria-label="Tamanho do texto da conversa" className="grid grid-cols-3 gap-1 rounded-xl border border-border bg-background/50 p-1">
                {([
                  { size: 12, label: "P", description: "Pequeno" },
                  { size: 14, label: "M", description: "Médio" },
                  { size: 16, label: "G", description: "Grande" },
                ] as const).map(({ size, label, description }) => (
                  <button
                    key={size}
                    type="button"
                    aria-pressed={value.messageFontSize === size}
                    aria-label={description + ", " + size + " px"}
                    onClick={() => set("messageFontSize", size)}
                    className={cn("min-h-11 rounded-lg px-2 py-1.5 text-center transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", value.messageFontSize === size ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted")}
                  >
                    <span className="block text-[12px] font-semibold">{label}</span>
                    <span className={cn("block text-[10px]", value.messageFontSize === size ? "text-primary-foreground/75" : "muted")}>{size} px</span>
                  </button>
                ))}
              </div>
              {![12, 14, 16].includes(value.messageFontSize ?? 14) && (
                <p className="muted text-[10px]">Tamanho atual: {value.messageFontSize ?? 14} px. Selecione P, M ou G para alterar.</p>
              )}
              <FontWeightPicker label="Peso da fonte das mensagens" value={value.messageFontWeight ?? 400} onChange={(next) => set("messageFontWeight", next)} />
              <p className="muted text-[10px]">O título do cabeçalho tem um ajuste independente na aba Perfil.</p>
            </div></div></div></section>
        </TabsContent>
      </Tabs>
    </div>
  )
}
